import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/utils/api';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { sessaoLiberada } from '@/features/auth/sessaoLiberada';
import { ponteDoAplicativo } from '@/features/aplicativo/aplicativo';
import { foraDaProva } from '@/features/relatos/podeRelatar';
import { CHAVE_DA_CONTAGEM, pedirContagem, usePreferencias, type Notificacao } from './api';
import { fraseComNome, fraseParaNovos, noSilencio, TITULO_FORA_DO_PORTAL } from './foraDoPortal';
import { nomeNoAvisoLigado } from './nomeNoAviso';

/**
 * A NOTIFICAÇÃO DO SISTEMA NO APLICATIVO DO COMPUTADOR (Plano B, etapa b — 19/10/2026). Só age dentro do aplicativo 2.0.12
 * em diante (a ponte com `notificar`); no navegador e no aplicativo antigo não faz nada. Montado UMA vez, no layout.
 *
 * - Pergunta o número de não lidas a cada minuto, MESMO com a janela atrás de outras (é o caso em que a notificação serve).
 * - Avisa quando o número SOBE (aviso novo, ou um lido que voltou a "não lido"); o aviso que só mudou de número, ainda não
 *   lido, não avisa de novo. Na abertura não avisa do que já estava lá — isso o sino mostra.
 * - O texto é GENÉRICO (`foraDoPortal.ts`): fora do portal o aviso fica à vista na tela. Com a opção "mostrar o nome do
 *   aluno" ligada NESTE aparelho (`nomeNoAviso.ts`), um aviso novo de entregas diz quem entregou por último
 *   ("Maria Silva (2º A) entregou o #1251"); mais de um aviso novo continua genérico. Usa a mesma ponte da 2.0.12.
 * - Silêncio (a preferência de Minha conta, 22h às 6h por padrão): nenhum aviso; o número no ícone continua.
 * - Aluno em prova: a contagem do servidor já esconde o que chegou depois do início (`corteDoAluno`); e na página da prova
 *   (`foraDaProva`) também não avisa.
 * - Com a janela à frente o aplicativo não mostra o aviso (o sino basta) — quem decide isso é o aplicativo (`main.js`).
 */
export function NotificacoesNoAplicativo() {
  const ponte = ponteDoAplicativo();
  const { isAuthenticated, user } = useAuth();
  const { pathname } = useLocation();
  const ativo = isAuthenticated && sessaoLiberada(user) && typeof ponte?.notificar === 'function';
  const { data: naoLidas } = useQuery<number>({
    // A MESMA chave e o mesmo pedido do sino (25/10/2026): era um segundo pedido por minuto.
    queryKey: [...CHAVE_DA_CONTAGEM],
    queryFn: pedirContagem,
    enabled: ativo,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
  });
  const { data: prefs } = usePreferencias(ativo);
  const anterior = useRef<number | null>(null);
  const desde = useRef<number>(Date.now());

  useEffect(() => {
    if (!ativo || naoLidas == null || !ponte) return;
    void ponte.contador?.(naoLidas);
    const antes = anterior.current;
    anterior.current = naoLidas;
    if (antes == null || naoLidas <= antes) { if (antes == null) desde.current = Date.now(); return; }
    const ultimaVez = desde.current;
    desde.current = Date.now();
    void (async () => {
      const itens = (await api.get<Notificacao[]>('/api/notificacoes', { params: { status: 'nao-lidas', limite: 10 } })).data;
      const novos = itens.filter((n) => new Date(n.atualizadaEm).getTime() > ultimaVez - 5_000);
      const comNome = nomeNoAvisoLigado(user?.id) ? fraseComNome(novos) : null;
      const corpo = comNome ?? fraseParaNovos(novos.map((n) => n.tipo));
      if (!corpo || !foraDaProva(pathname)) return;
      if (prefs && noSilencio(new Date(), prefs.silencio)) return;
      await ponte.notificar?.({ titulo: TITULO_FORA_DO_PORTAL, corpo });
    })().catch(() => undefined);
  }, [naoLidas]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
